import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, type SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import {
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
} from '@mui/material';
import _ from 'lodash';
import { UserPlus } from 'lucide-react';
import * as z from 'zod';

import Button from '../../common/Button';
import EmailInputChips from '../../common/EmailInputChips';
import { extractValue, ProjectRolesOptions } from '../../common/utils';
import { useShareModel } from '../../hooks/tanstack/useSharedModelsHook';
import { useGetUser } from '../../hooks/tanstack/useUserHook';
import {
  useGetDistinguishModel,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import { handleQueryError } from '../../utils/api';
import cn from '../../utils/cn';
import FormInput from '../form/FormInput';
import SuccessDialog from '../SuccessDialog';

const EmailSchema = z.string().email('Invalid email address');

type EmailSchemaType = z.infer<typeof EmailSchema>;

type ShareModelFormDialogProps = {
  trigger?: {
    label: React.ReactNode;
    style?: React.CSSProperties;
  };
};

const ShareModelFormDialog = ({ trigger }: ShareModelFormDialogProps) => {
  const inputId = 'emails';
  const [open, setOpen] = useState(false);

  const { projectId, modelId, orgId } = useGetRouteParams();

  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [error, setError] = useState<Record<string, string>>({});

  const [emails, setEmails] = useState<{ value: string; isError?: boolean }[]>(
    []
  );

  const ref = useRef<HTMLInputElement>(null);

  const form = useForm({
    defaultValues: {
      email: '',
      emails: [],
      separate_guests: false,
      role: ProjectRolesOptions[0].value,
      shared_model_name: '',
      shared_model_description: '',
    },
  });

  const {
    formState,
    setError: setFormError,
    reset,
    register,
    clearErrors,
    watch,
  } = form;

  const { isSubmitting, errors } = formState;

  const { data: currentUser } = useGetUser();

  const { model, modelOutput } = useGetDistinguishModel();

  const role = 'project_owner';

  useEffect(() => {
    if (error) {
      if (error instanceof String) {
        setFormError(inputId, {
          type: 'custom',
          message: error as unknown as string,
        });
        return;
      }
    }
  }, [error]);

  const { mutateAsync: shareModel, isPending: isSharingModel } = useShareModel({
    onSuccess: () => {
      handleOpenSuccessDialog();
    },
    onError(error) {
      clearErrors();
      handleQueryError(error, setError);
    },
  });

  const handleOpenSuccessDialog = () => {
    setOpenSuccessDialog(true);
  };

  const handleCloseSuccessDialog = () => {
    setOpenSuccessDialog(false);
  };

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    reset();
    setEmails([]);
    clearErrors();
    setError({});
  };

  const handleError = ({
    message,
    email,
    ref,
  }: {
    email: string;
    message: string;
    ref: React.RefObject<HTMLInputElement>;
  }) => {
    setFormError(`${inputId}.${email.replaceAll('.', '')}` as any, { message });
    setEmails((prev) => [
      ...prev,
      {
        value: email,
        isError: true,
      },
    ]);
    if (ref.current) {
      ref.current.value = '';
    }
  };

  const handleAddEmail = (email: EmailSchemaType) => {
    const { error } = EmailSchema.safeParse(email);

    if (error) {
      handleError({
        email,
        message: error.errors[0].message,
        ref,
      });
      return;
    }

    if (currentUser?.email === email) {
      handleError({
        email,
        message: "You're not able to share to yourself.",
        ref,
      });
      return;
    }

    setEmails((prev) => [...prev, { value: email }]);

    if (ref.current) {
      ref.current.value = '';
    }
  };

  const handleRemoveEmail = (email: string) => {
    setEmails((prev) => prev.filter((e) => e.value !== email));
    clearErrors(`${inputId}.${email.replaceAll('.', '')}` as any);
  };

  const onSubmit: SubmitHandler<any> = async () => {
    if (!model || !role) {
      return;
    }

    const currentEmail = ref.current?.value;

    if (!emails.length && !currentEmail) {
      setError({ [inputId]: 'You must enter at least one email' });
      clearErrors();
      return;
    }

    if (currentEmail === currentUser?.email) {
      setFormError(`${inputId}.${currentEmail?.replaceAll('.', '')}` as any, {
        message: "You're not able to share to yourself.",
      });
      return;
    }

    const { separate_guests, shared_model_name, shared_model_description } =
      watch();

    if (currentEmail) {
      await shareModel({
        projectId,
        modelId,
        orgId,
        data: {
          separate_guests,
          filters: model.filters,
          shared_model_name,
          shared_model_description,
          guests: [
            {
              email: currentEmail,
              role,
            },
          ],
        },
      });
    } else {
      await shareModel({
        projectId,
        modelId,
        orgId,
        data: {
          separate_guests,
          filters: model.filters,
          guests: emails.map((email) => ({
            email: email.value,
            role,
          })),
        },
      });
    }

    setEmails([]);
    reset();
    setOpen(false);
    return;
  };

  const isDisabled = isSubmitting || !!emails.find((e) => e.isError) || false;

  const errorsKeys = Object.keys(errors[inputId] ?? {});

  const errorMessage = useMemo(
    () =>
      errors[inputId]
        ? (errors[inputId] as unknown as Record<string, { message: string }>)[
            errorsKeys[errorsKeys.length - 1]
          ]?.message
        : typeof error === 'string'
          ? (error as string)
          : extractValue(error),
    [errorsKeys, error]
  );

  const isLoading = isSharingModel;

  return (
    <div>
      <Tooltip
        title={
          _.isEmpty(modelOutput)
            ? "Can't share due to missing model output, either click or wait till the calculation finish running."
            : ''
        }
      >
        <Button
          type="button"
          variant={_.isEmpty(modelOutput) ? 'outlined' : 'outlined_red'}
          onClick={() => {
            if (_.isEmpty(modelOutput)) {
              return;
            }
            handleOpenDialog();
          }}
        >
          {trigger?.label ?? (
            <>
              <UserPlus width={20} height={20} />
              <p>Share Model</p>
            </>
          )}
        </Button>
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => {
          if (isLoading) {
            return;
          }

          handleCloseDialog();
        }}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiPaper-root': {
            overflowY: 'visible',
          },
        }}
      >
        <DialogTitle className="w-full">
          <h2 className="pt-2 text-xl font-bold capitalize">
            Share this model
          </h2>
        </DialogTitle>
        <DialogContent
          style={{
            overflowY: 'visible',
          }}
        >
          <FormProvider {...form}>
            <form>
              <div className="mb-2 space-y-6">
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col">
                    <label
                      htmlFor="email"
                      className="min-h-[28px] font-semibold pb-2 text-sm text-[#666]"
                    >
                      Email address(es)
                    </label>
                    <div
                      className={cn(
                        'flex items-center justify-between rounded-[4px] border-2 border-[#2196F3] px-2',
                        {
                          'border-primary-red': errorMessage,
                        }
                      )}
                    >
                      <div className="flex w-full flex-wrap items-center gap-1 py-2">
                        <EmailInputChips
                          emails={emails}
                          onRemoveEmail={handleRemoveEmail}
                        />
                        <input
                          ref={ref}
                          name="email"
                          type="email"
                          onKeyDown={(e) => {
                            const keyCode = e.key;

                            const isEmpty =
                              e.currentTarget.value.replace(',', '').length ===
                              0;

                            if (isEmpty && ref.current) {
                              ref.current.value = '';
                              return;
                            }

                            if (keyCode === 'Enter' || keyCode === ',') {
                              e.preventDefault();
                              handleAddEmail(
                                e.currentTarget.value.replace(',', '')
                              );
                              return;
                            }
                          }}
                          className="grow outline-none"
                        />
                      </div>
                      <p className="capitalize min-w-[100px] text-sm">
                        Project Owner
                      </p>
                    </div>
                  </div>
                  <FormInput
                    name="shared_model_name"
                    label="Shared Model Name"
                    disabled={isLoading}
                    placeholder={model?.name}
                  />
                  <FormInput
                    name="shared_model_description"
                    label="Description"
                    multiline
                    disabled={isLoading}
                    placeholder={model?.description}
                    rows={4}
                  />
                  <div className="w-full">
                    <label
                      htmlFor="separate_guests"
                      className="group flex cursor-pointer items-center py-3"
                    >
                      <Checkbox
                        id="separate_guests"
                        {...register('separate_guests')}
                      />
                      <p>
                        Send individual invitation to each guest separately.
                      </p>
                    </label>
                  </div>
                  {errorMessage && (
                    <p className="px-2 text-sm text-primary-red">
                      {errorMessage}
                    </p>
                  )}
                </div>
                <div className="flex-end flex justify-end gap-3">
                  <Button
                    onClick={handleCloseDialog}
                    variant="outlined"
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    loading={isLoading}
                    disabled={isDisabled}
                    onClick={onSubmit}
                    variant="contained_blue"
                  >
                    Send
                  </Button>
                </div>
              </div>
            </form>
          </FormProvider>
        </DialogContent>
      </Dialog>
      <SuccessDialog
        open={openSuccessDialog}
        handleOnClose={handleCloseSuccessDialog}
        content="We've sent the invitation to approved users."
      />
    </div>
  );
};

export default ShareModelFormDialog;
